import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Patient } from '@/types/Patient';
import Physician from '../../../../../types/Physician';
import { Procedure, ProcedureType } from '@/types/Procedure';
import { Room } from '@/types/Room';
import moment from 'moment-timezone';
import { useScope } from '@/hooks/useScope';
import { ProcedureStep } from '@/types/ProcedureStep';

import { ProcedureState } from '../util/procedureSteps';
import { getLogEntries, isHelpFn, isFlipRoomFn } from '../utils';
import { PATIENT_EVENT_TYPES } from '../../../../entities/patient/enums';
import { OR } from '../../../../entities/room/enums';
import { alpha, Box, CssBaseline, makeStyles, SvgIcon, SvgIconProps } from '@material-ui/core';
import { setHelp, setUndoHelp, setFlipRoom, setUndoFlipRoom } from '@/graph/patients';
import { useMutation } from '@apollo/client';
import Mousetrap from 'mousetrap';
import Divider from '@material-ui/core/Divider';
import { ThemeProvider } from '@material-ui/core/styles';
import { tabletThemeDark } from '../../../../../themes/tabletTheme';
import { useScreenConfiguration } from '@/contexts/ScreenConfigurationContext';
import { ProcedureStepsColumn } from '@/components/pages/kiosk/tablet/v2/procedure/ProcedureStepsColumn';
import { ProcedureControlsSection } from '@/components/pages/kiosk/tablet/v2/procedure/ProcedureControlsSection';
import { ProcedureFooterSection } from '@/components/pages/kiosk/tablet/v2/procedure/ProcedureFooterSection';
import { ProcedureHeaderSection } from '@/components/pages/kiosk/tablet/v2/procedure/ProcedureHeaderSection';
import { isDirectProcedureStep, sequenceSteps } from '../util/procedureStepCapture';

export function FlipRoom(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <style type="text/css">{`.st0{fill:#FFFFFF;}`}</style>
        <g>
          <path
            className="st0"
            d="M5.8,16.7l0-0.3c0-1.8,0.3-3.4,0.9-4.9c0.7-1.7,1.9-3,3.5-4.1c1.4-1,3-1.6,4.7-1.7c1.2-0.1,2.4,0,3.5,0.2
		c1.2,0.3,2.4,0.8,3.5,1.6c-0.2,0.3-0.5,0.6-0.7,0.9c-0.4,0.5-0.2,0.9,0,1.1c0.2,0.1,0.4,0.2,0.6,0.2c0.9,0,1.8,0.1,2.6,0.1l0.8,0
		c0.3,0,0.6-0.1,0.7-0.3c0.2-0.3,0.1-0.7,0.1-0.8l-0.1-0.3c0-0.1-0.1-0.3-0.1-0.4l-0.7-2.8c-0.1-0.4-0.3-0.7-0.7-0.7
		c-0.4-0.1-0.7,0.2-0.8,0.4l-0.2,0.3c-0.1,0.1-0.2,0.3-0.3,0.4c0,0,0,0,0,0c-0.3-0.2-0.6-0.4-0.9-0.6c-1.9-1.1-3.8-1.7-5.8-1.8
		C15,3.3,13.4,3.5,11.9,4c-1.3,0.5-2.5,1.1-3.6,2c-1.9,1.6-3.3,3.5-4.1,5.8c-0.6,1.6-0.7,3.4-0.6,5.3c0,0.3,0.1,0.6,0.2,0.8
		c0.2,0.3,0.5,0.5,0.9,0.5c0.1,0,0.2,0,0.3,0c0.5-0.1,0.8-0.6,0.8-1.1C5.8,17.2,5.8,17,5.8,16.7z"
          />
          <path
            className="st0"
            d="M28.2,14.2c-0.2-0.5-0.7-0.8-1.3-0.6c-0.5,0.1-0.9,0.6-0.8,1.1c0,0.1,0,0.2,0,0.3l0,0.2
		c0.1,1.4-0.1,2.8-0.5,4.1c-0.5,1.5-1.2,2.6-2.1,3.6c-1.8,1.8-3.7,2.9-6,3.2c-1.2,0.2-2.5,0.2-3.7,0c-1-0.2-2-0.6-3-1.1
		c-0.3-0.2-0.6-0.3-0.8-0.5l-0.1,0l0.2-0.3c0.2-0.2,0.4-0.5,0.6-0.8c0.2-0.2,0.2-0.5,0.1-0.8c-0.1-0.3-0.4-0.4-0.7-0.4l-1.6,0
		c-0.7,0-1.3,0-2-0.1c-0.4,0-0.6,0.1-0.7,0.3c-0.1,0.1-0.2,0.4-0.1,0.7c0.1,0.4,0.2,0.7,0.3,1.1l0.6,2.6c0.1,0.3,0.3,0.5,0.6,0.6
		c0.3,0.1,0.6,0,0.8-0.3C8.1,27,8.1,27,8.2,26.9l0.4-0.6C9,26.5,9.3,26.8,9.7,27c1.7,1,3.6,1.5,5.7,1.6c0.2,0,0.4,0,0.7,0
		c2,0,3.9-0.5,5.7-1.4c2.5-1.3,4.4-3.3,5.6-5.8c0.9-1.9,1.3-4,1.2-6.2C28.4,14.9,28.4,14.5,28.2,14.2z"
          />
          <path
            className="st0"
            d="M13.4,11.5c-1.5-0.3-2.8,0.1-3.8,1.3c-0.6,0.8-1,1.7-1,2.9l0,0.2c0,0.2,0,0.3,0,0.5c0.3,2,1.6,3.3,3.5,3.5
		c0.1,0,0.3,0,0.4,0c1.7,0,3.2-1.1,3.7-2.7c0.2-0.8,0.3-1.6,0.1-2.5C16.1,13.1,15,11.8,13.4,11.5z M14.3,15.7c0,0.7-0.2,1.1-0.4,1.5
		c-0.4,0.5-1,0.8-1.7,0.6c-0.7-0.1-1.1-0.5-1.3-1.3c-0.2-0.7-0.1-1.3,0.1-1.9c0.3-0.7,0.8-1.1,1.5-1.1c0,0,0,0,0.1,0
		c0.9,0,1.4,0.7,1.6,1.3C14.3,15.1,14.3,15.3,14.3,15.7z"
          />
          <path
            className="st0"
            d="M16.8,18.9c0,0.5,0.4,0.9,0.9,0.9c0.2,0,0.3,0,0.5,0c0.4,0,0.7-0.3,0.7-0.7c0-0.1,0-0.3,0-0.4l0-1.8
		c0.1,0,0.3,0,0.4,0l0.6,1c0.2,0.4,0.5,0.8,0.8,1.2c0.2,0.3,0.5,0.5,1,0.5c0,0,0.1,0,0.1,0c0.2,0,0.3,0,0.5,0c0.3,0,0.5-0.1,0.6-0.3
		c0.1-0.2,0.1-0.4-0.1-0.7l-1.4-2.2c0,0,0.1,0,0.1-0.1c0.2-0.1,0.4-0.2,0.6-0.4c0.5-0.5,0.7-1.2,0.7-2.1c0-1-0.5-1.8-1.3-2.2
		c-0.4-0.2-1-0.3-1.5-0.3c0,0,0,0,0,0l-2.4,0c-0.2,0-0.5,0-0.7,0.2c-0.2,0.2-0.2,0.5-0.2,0.7l0,5.4l0,0.4
		C16.8,18.5,16.8,18.7,16.8,18.9z M19.2,13.5c0.4,0,0.8,0,1.3,0.1c0.1,0,0.4,0.1,0.4,0.6c0,0.5-0.2,0.7-0.5,0.7
		c-0.2,0-0.5,0.1-0.7,0.1l-0.2,0c-0.1,0-0.3,0-0.4,0v0c0-0.5,0-1,0-1.5L19.2,13.5z"
          />
        </g>
      </svg>
    </SvgIcon>
  );
}

const ProcedureHandling = ({
  status,
  room,
  patient,
  physician,
  procedureType,
  editableAnesthesiologist,
  procedureState,
  nextProceduresInOperationRoom,
  notifyPhysician,
  isPreviousPatient = false,
  previousPatientExit,
  initialFlipRoomState,
  duration,
  delayReasonLabel,
  delayReasonSkipped,
  onDelayReasonClick,
}: {
  status: string;
  room: Room & { status: string; patient: Patient & { duration?: string } };
  patient?: Patient & { duration?: string };
  physician?: Physician;
  procedureType?: ProcedureType;
  procedureState: ProcedureState | null;
  nextProceduresInOperationRoom: Procedure[];
  editableAnesthesiologist?: boolean;
  notifyPhysician: () => Promise<void>;
  isPreviousPatient?: boolean;
  previousPatientExit?: () => void;
  initialFlipRoomState?: boolean;
  duration?: { duration?: string };
  delayReasonLabel?: string;
  delayReasonSkipped?: boolean;
  onDelayReasonClick?: () => void;
}) => {
  const patientId = patient?.id;
  const visibleSteps = sequenceSteps(procedureState?.steps || []);
  const steps1 = visibleSteps.slice(0, 4);
  const steps2 = visibleSteps.slice(4, 8);
  const directStep = [...(procedureState?.directSteps || []), ...(procedureState?.steps || [])].find(
    isDirectProcedureStep
  );

  const classes = useStyles();

  const logEntries = useMemo(() => getLogEntries(patient), [patient]);

  const [isHelpActive, setIsHelpActive] = useState(() => isHelpFn(logEntries));

  // Initialize flip room state with either the passed in value or the computed value
  const [isFlipRoomActive, setIsFlipRoomActive] = useState(() =>
    initialFlipRoomState !== undefined ? initialFlipRoomState : isFlipRoomFn(logEntries)
  );

  // Update flip room state when initialFlipRoomState changes
  useEffect(() => {
    if (initialFlipRoomState !== undefined) {
      setIsFlipRoomActive(initialFlipRoomState);
    }
  }, [initialFlipRoomState]);

  const [doHelp] = useMutation(setHelp, {
    onCompleted: () => {
      setIsHelpActive(true);
    },
  });

  const [doUndoHelp] = useMutation(setUndoHelp, {
    onCompleted: () => {
      setIsHelpActive(false);
    },
  });

  const [doFlipRoom] = useMutation(setFlipRoom, {
    onCompleted: () => {
      setIsFlipRoomActive(true);
    },
  });

  const [doUndoFlipRoom] = useMutation(setUndoFlipRoom, {
    onCompleted: () => {
      setIsFlipRoomActive(false);
    },
  });

  const handleHelp = useCallback(async () => {
    if (!patientId) {
      console.warn('Patient ID is not available');
      return;
    }

    if (isHelpActive) {
      await doUndoHelp({
        variables: {
          id: patientId,
        },
      });
    } else {
      await doHelp({
        variables: {
          id: patientId,
        },
      });
    }
  }, [doHelp, doUndoHelp, isHelpActive, patientId]);

  const handleFlipRoom = useCallback(async () => {
    if (!patientId) {
      console.warn('Patient ID is not available');
      return;
    }

    if (isFlipRoomActive) {
      await doUndoFlipRoom({
        variables: {
          id: patientId,
        },
      });
    } else {
      await doFlipRoom({
        variables: {
          id: patientId,
        },
      });
    }
  }, [doFlipRoom, doUndoFlipRoom, isFlipRoomActive, patientId]);

  const helpHandlerRef = useRef(handleHelp);
  const flipRoomHandlerRef = useRef(handleFlipRoom);

  useEffect(() => {
    helpHandlerRef.current = handleHelp;
  }, [handleHelp]);

  useEffect(() => {
    flipRoomHandlerRef.current = handleFlipRoom;
  }, [handleFlipRoom]);

  useEffect(() => {
    const handleUndoHelp = async () => {
      if (status === 'AnestheticEnd' && isHelpActive) {
        try {
          await doUndoHelp({
            variables: {
              id: patientId,
            },
          });
        } catch (error) {
          console.error('Error calling undoHelp:', error);
        }
      }
    };

    handleUndoHelp().then();
  }, [status, isHelpActive, doUndoHelp, patientId]);

  useEffect(() => {
    const handleUndoFlipRoom = async () => {
      if (status === 'AnestheticEnd' && isFlipRoomActive) {
        try {
          await doUndoFlipRoom({
            variables: {
              id: patientId,
            },
          });
        } catch (error) {
          console.error('Error undoing flip room.', error);
        }
      }
    };

    handleUndoFlipRoom().then();
  }, [status, isFlipRoomActive, doUndoFlipRoom, patientId]);

  // help button handler
  useEffect(() => {
    const handleKeyPress = () => {
      helpHandlerRef
        .current()
        .then()
        .catch(error => {
          console.error('Error handling help:', error);
        });
    };

    Mousetrap.bind('h', handleKeyPress);

    return () => {
      Mousetrap.unbind('h');
    };
  }, []);

  // flip room handler
  useEffect(() => {
    const handleKeyPress = () => {
      flipRoomHandlerRef
        .current()
        .then()
        .catch(error => {
          console.error('Error handling flip room:', error);
        });
    };

    Mousetrap.bind('f', handleKeyPress);

    return () => {
      Mousetrap.unbind('f');
    };
  }, []);

  const { enableFlipRoom, enableHelp } = useScreenConfiguration();

  const latestExitByRoomType = useMemo(() => {
    if (!isPreviousPatient) {
      return {};
    }

    return logEntries
      .filter(event => event.type === PATIENT_EVENT_TYPES.EXITED && event?.room?.type)
      .reduce<Record<string, Date | null>>((acc, event) => {
        const { room, timestamp } = event;
        if (!room?.type || !timestamp) {
          return acc;
        }

        const eventDate = new Date(timestamp);
        const current = acc[room.type];
        if (!current || eventDate.getTime() > current.getTime()) {
          acc[room.type] = eventDate;
        }
        return acc;
      }, {});
  }, [isPreviousPatient, logEntries]);

  const getPatientExitedRoomTime = useCallback(
    (roomType: string) => {
      if (!isPreviousPatient) {
        return null;
      }

      const latest = latestExitByRoomType[roomType];
      return latest ?? null;
    },
    [isPreviousPatient, latestExitByRoomType]
  );

  const orOutTime = getPatientExitedRoomTime(OR);
  const timezone = useScope().hospital?.timezone?.id || 'UTC';
  const formattedOrOutTime = orOutTime ? moment(orOutTime).tz(timezone).format('HH:mm') : null;

  return (
    <ThemeProvider theme={tabletThemeDark}>
      <CssBaseline />
      <Box className={classes.root}>
        <ProcedureHeaderSection
          directStep={directStep}
          roomId={room.id}
          isPreviousPatient={isPreviousPatient}
          previousPatientExit={previousPatientExit}
          status={status}
          physician={physician}
          patient={patient}
          procedureType={procedureType}
          editableAnesthesiologist={editableAnesthesiologist}
          notifyPhysician={notifyPhysician}
          duration={duration}
          enableFlipRoom={!!enableFlipRoom}
          enableHelp={!!enableHelp}
          handleFlipRoom={handleFlipRoom}
          handleHelp={handleHelp}
          isFlipRoomActive={isFlipRoomActive}
          isHelpActive={isHelpActive}
        />

        <Divider />

        <Box className={classes.main}>
          <ProcedureStepsColumn
            steps={steps1 as (ProcedureStep & { highlight: boolean; show: boolean })[]}
            patient={patient}
            isPreviousPatient={isPreviousPatient}
            containerStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          />

          <Divider orientation="vertical" />

          <ProcedureControlsSection
            roomName={room.name}
            roomStatus={room?.status}
            procedureState={procedureState}
            isPreviousPatient={isPreviousPatient}
            formattedOrOutTime={formattedOrOutTime}
          />

          <Divider orientation="vertical" />

          <ProcedureStepsColumn
            steps={steps2 as (ProcedureStep & { highlight: boolean; show: boolean })[]}
            patient={patient}
            isPreviousPatient={isPreviousPatient}
            containerStyle={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          />
        </Box>

        <Divider />

        <ProcedureFooterSection
          roomName={room?.name}
          isPreviousPatient={isPreviousPatient}
          nextProceduresInOperationRoom={nextProceduresInOperationRoom}
          status={status}
          patient={patient}
          steps={procedureState?.steps}
          delayReasonLabel={delayReasonLabel}
          delayReasonSkipped={delayReasonSkipped}
          onDelayReasonClick={onDelayReasonClick}
        />
      </Box>
    </ThemeProvider>
  );
};

export const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    gap: theme.spacing(4),
  },
  header: {
    display: 'flex',
    gap: '1rem',
  },
  headerColumn: {
    flex: 1,
    minWidth: 0,
  },
  main: {
    flex: 1,
    display: 'flex',
    gap: theme.spacing(4),
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
    gap: theme.spacing(2),
  },
  mainValue: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  subtext: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  helpButton: {
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    borderWidth: 4,
    borderStyle: 'solid',
    borderRadius: 8,
    borderColor: 'rgba(255,255,255,0.25)',
    height: '100%',
    minWidth: 128,
    position: 'relative',
    overflow: 'visible',
  },
  helpButtonActive: {
    backgroundColor: '#FF4842',
    '&:before': {
      content: '""',
      animation: `$myEffect 1500ms ${theme.transitions.easing.sharp} infinite`,
      display: 'block',
      position: 'absolute',
      height: '100%',
      width: '100%',
      backgroundColor: '#FF4842',
      transformOrigin: 'center center',
      borderRadius: 8,
      zIndex: -1,
    },
  },
  flipRoomButton: {
    backgroundColor: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    borderWidth: 4,
    borderStyle: 'solid',
    borderRadius: 8,
    borderColor: '#6c9911',
    height: '100%',
    minWidth: 128,
    position: 'relative',
    overflow: 'visible',
  },
  flipRoomButtonActive: {
    backgroundColor: 'transparent',
    '&:before': {
      content: '""',
      // animation: `$myEffect 1500ms ${theme.transitions.easing.sharp} infinite`,
      display: 'block',
      position: 'absolute',
      height: '100%',
      width: '100%',
      backgroundColor: alpha('#6c9911', 0.25),
      transformOrigin: 'center center',
      zIndex: -1,
    },
  },
  '@keyframes myEffect': {
    '0%': {
      opacity: 1,
      transform: 'scale(1)',
    },
    '100%': {
      opacity: 0,
      transform: 'scaleX(1.25) scaleY(1.3)',
    },
  },
}));

export default ProcedureHandling;
